import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared, type ErrorView } from '@krp/client-runtime'

import type { CommandStatus } from '../../../shared/model/commandOutcome'
import { ownedBy } from './keys'
import type { ProductOperation, ProductOperationResult, ProductOperationsState } from './types'

/**
 * Пишущие операции товаров 1С: идентичность команды и её итог. Данные товара
 * сюда не попадают, только итог команды. Срок — `screen`: пока команда в пути
 * или её исход неизвестен, окно предупреждает об уходе со страницы
 * (перезагрузка потеряла бы ключ для проверки исхода). Сохранение ключа в
 * sessionStorage — запрос интегратору (evidence UB1a).
 */
const initialState: ProductOperationsState = {}

export interface OperationSettled {
  key: string
  /** Итог пишется только своей операции: ответ старой команды не затирает новую. */
  operationId: string
  status: Exclude<CommandStatus, 'sending'>
  resendable: boolean
  error: ErrorView | null
  result: ProductOperationResult | null
}

const operationsSlice = createSlice({
  name: 'onec/productOperations',
  initialState,
  reducers: {
    operationStarted(state, action: PayloadAction<ProductOperation>) {
      const current = state[action.payload.key]
      // Пока прежняя команда в пути или её исход неизвестен, новую не начинаем: двойная запись.
      if (current !== undefined && (current.status === 'sending' || current.status === 'unknown')) return
      return { ...state, [action.payload.key]: action.payload }
    },
    /** Чтение перед командой закончено: данные и ревизия команды известны. Только для своей операции. */
    operationPrepared(
      state,
      action: PayloadAction<Pick<ProductOperation, 'key' | 'operationId' | 'payload' | 'expectedConfigurationVersion'>>,
    ) {
      const operation = state[action.payload.key]
      if (operation === undefined || operation.operationId !== action.payload.operationId) return
      operation.payload = action.payload.payload
      operation.expectedConfigurationVersion = action.payload.expectedConfigurationVersion
    },
    /** Команда не понадобилась (требуемое уже в 1С): операция ещё ничего не отправляла. */
    operationCancelled(state, action: PayloadAction<{ key: string; operationId: string }>) {
      if (state[action.payload.key]?.operationId === action.payload.operationId) delete state[action.payload.key]
    },
    operationSettled(state, action: PayloadAction<OperationSettled>) {
      const operation = state[action.payload.key]
      if (operation === undefined || operation.operationId !== action.payload.operationId) return
      operation.status = action.payload.status
      operation.resendable = action.payload.resendable
      operation.error = action.payload.error
      operation.result = action.payload.result
    },
    operationResent(state, action: PayloadAction<{ key: string }>) {
      const operation = state[action.payload.key]
      if (operation === undefined) return
      operation.status = 'sending'
      operation.error = null
    },
    operationDismissed(state, action: PayloadAction<{ key: string }>) {
      const operation = state[action.payload.key]
      // Неизвестный исход не скрывается: забыть ключ — значит потерять способ проверить.
      if (operation === undefined || operation.status === 'sending' || operation.status === 'unknown') return
      // Подтверждённая пометка удаления — факт о товаре, а не уведомление: держит правку закрытой.
      if (operation.result?.kind === 'deleted' && operation.result.deleted) return
      delete state[action.payload.key]
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => {
      for (const key of Object.keys(state)) if (ownedBy(key, action.payload.resourceId)) delete state[key]
    })
  },
})

export const {
  operationStarted,
  operationPrepared,
  operationCancelled,
  operationSettled,
  operationResent,
  operationDismissed,
} = operationsSlice.actions
export const productOperationsReducer = operationsSlice.reducer
